<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { STATUS_OPEN, STATUS_CLOSED } from '@/constants';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import ProgressSpinner from 'primevue/progressspinner';
import { useDialog } from 'primevue/usedialog';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import ToggleSwitch from 'primevue/toggleswitch';
import TableColumn from 'primevue/column';
import Tag from 'primevue/tag';
import ManageDestinationGrouping from './ManageDestinationGrouping.vue';

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const dialog = useDialog();
const packageManagementStore = usePackageManagementStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const destinationGroupingList = ref([]);
const destinationGroupingFilters = ref();
const loading = ref(false);
const statuses = ref([STATUS_OPEN, STATUS_CLOSED]);

const getListDestinationGroup = () => {
  packageManagementStore.getListDestinationGroup().then((response) => {
    destinationGroupingList.value = response.data;
  });
};
getListDestinationGroup();

const initDestinationGroupingFilters = () => {
  destinationGroupingFilters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    airportCode: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destinationNameId: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destination: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    status: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initDestinationGroupingFilters();

const getSeverityStatus = (status: string) => {
  switch (status) {
    case STATUS_CLOSED:
      return 'danger';
    case STATUS_OPEN:
      return 'info';
  }
};

const updateDestinationGroupStatus = (sk: string, status: string) => {
  packageManagementStore
    .updateDestinationGroupStatus(sk, status)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
    });
};

const openDialog = (header: string, data?: any) => {
  const dialogRef = dialog.open(ManageDestinationGrouping, {
    data: {
      data: data
    },
    props: {
      header: t(header),
      modal: true
    },
    emits: {
      closeModal: () => {
        dialogRef.close();
        getListDestinationGroup();
      }
    }
  });
};

const editDestinationGroup = (data) => {
  openDialog('management.package.add_destination_grouping', data);
};

const deleteDestinationGroup = (data) => {
  packageManagementStore
    .deleteDestinationGroup(data.sk)
    .then(() => {
      toast.add({ severity: 'success', summary: '', detail: t('notification.delete_successfully'), life: 3000 });
      getListDestinationGroup();
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.delete_error'), life: 5000 });
    });
};

const newlineFromString = (string: string) => {
  const newArr = string.split(',');
  return newArr;
};

const addDestinationGroup = () => {
  openDialog('management.package.add_destination_grouping');
};

const formatData = (data) => {
  if (!data) {
    return [];
  }
  const result = data.map((item) => {
    const destinationFormat = item.groupDes?.map((des, index) => `${index ? ' ' : ''}${des.name} (${des.code})`);
    item.destination = destinationFormat.toString();
    item.destinationNameId = `${item.destName} (${item.destCode})`;
    return item;
  });
  return result;
};

const canModifyGroup = authorizations?.configurationPage.destination_groupings_edit;
</script>

<template>
  <div class="card">
    <div class="flex justify-content-end">
      <Button id="add-rule-btn" v-if="canModifyGroup" type="button" icon="pi pi-plus" :label="$t('management.package.add_rule')" class="p-button-primary mb-2" @click="addDestinationGroup()" />
    </div>
    <DataTable
      v-model:filters="destinationGroupingFilters"
      :value="formatData(destinationGroupingList)"
      paginator
      showGridlines
      :loading="loading"
      :rows="10"
      dataKey="id"
      filterDisplay="menu"
      :globalFilterFields="['airportCode', 'destCode', 'destination', 'status']"
    >
      <template #header>
        <div class="flex justify-content-between flex-col sm:flex-row">
          <div class="flex justify-content-left flex-col sm:flex-row gap-3">
            <Button id="clear-filter-btn" type="button" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="initDestinationGroupingFilters()" />
          </div>
          <div class="justify-content-right">
            <span class="p-input-icon-left mb-2">
              <i class="pi pi-search" />
              <InputText id="input_search" v-model="destinationGroupingFilters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
            </span>
          </div>
        </div>
      </template>
      <template #empty> {{ $t('management.no_data_found') }} </template>
      <template #loading>
        <ProgressSpinner class="w-4rem h-4rem" strokeWidth="8" animationDuration="2s" />
      </template>
      <TableColumn field="airportCode" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span>{{ $t('management.package.airport_code') }}</span>
          </div>
        </template>
        <template #body="{ data, field }">
          <span class="long-text" v-tooltip="data[field]">
            {{ data[field] }}
          </span>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_airport_code')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn field="destinationNameId" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span>{{ $t('management.package.destination_id') }}</span>
          </div>
        </template>
        <template #body="{ data, field }">
          <div class="flex flex-col">
            <span>{{ data[field] }} </span>
          </div>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination_id')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn field="destination" :showAddButton="false" :showFilterOperator="false">
        <template #header>
          <div class="flex flex-col">
            <span>{{ $t('management.package.mapped_destination') }}</span>
          </div>
        </template>
        <template #body="{ data }">
          <p v-for="(item, index) in newlineFromString(data.destination)" :key="index">{{ item }}</p>
        </template>
        <template #filter="{ filterModel }">
          <InputText v-model="filterModel.value" type="text" class="p-column-filter w-16rem" :placeholder="$t('management.package.search_by_destination')" />
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
      <TableColumn v-if="canModifyGroup" :header="$t('common.action')">
        <template #body="{ data }">
          <div class="flex">
            <Button icon="pi pi-pencil" class="p-button-text" @click="editDestinationGroup(data)" />
            <Button icon="pi pi-trash" class="p-button-text" @click="deleteDestinationGroup(data)" />
          </div>
        </template>
      </TableColumn>
      <TableColumn field="status" :header="$t('common.status')" :showAddButton="false" :showFilterOperator="false">
        <template #body="{ data }">
          <ToggleSwitch :disabled="!canModifyGroup" v-model="data.status" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" @change="updateDestinationGroupStatus(data.sk, data.status)" />
        </template>
        <template #filter="{ filterModel }">
          <Select v-model="filterModel.value" :options="statuses" :placeholder="$t('common.select_one')" class="p-column-filter" showClear>
            <template #option="slotProps">
              <Tag :value="slotProps.option" :severity="getSeverityStatus(slotProps.option)" />
            </template>
          </Select>
        </template>
        <template #filterclear="{ filterCallback }">
          <Button :label="$t('common.buttons.clear')" @click="filterCallback()" class="clear-filter-btn"></Button>
        </template>
      </TableColumn>
    </DataTable>
  </div>
</template>
